import { A, useLocation } from '@solidjs/router';
import clsx from 'clsx';
import { For, Match, Show, Switch, createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import { useAppContext } from '../../../app-context-provider/app-context-provider';
import {
    ColorName,
    textColorClasses,
    bgHoverColorClasses,
    bgFocusColorClasses,
    borderColorClasses,
    borderHoverColorClasses,
    borderFocusColorClasses,
} from '../../../style/color-classes';
import { ChevronUpBoldIcon } from '../../icons-library/chevron-up-bold';
import { SiteLogo } from '../../icons-library/site-logo';
import { MenuProps } from '../menu-types.d';
import { SearchIcon } from '../../icons-library/search';
import { MobileSearch } from '../../search/search';
import { Portal } from 'solid-js/web';

export const MenuDesktop = (props: MenuProps) => {
    const { currentHeaderColors, translate } = useAppContext();

    const navClassNames = 'flex justify-center items-center gap-1 mt-2 desktop:mt-0 desktop:absolute desktop:top-0 desktop:right-0 desktop:px-8 desktop:min-h-headerDesktop';
    const middleNavClassNames = 'flex justify-center items-center gap-2 mt-2 desktop:mt-0 desktop:absolute desktop:min-h-headerDesktop';
    const [activeSubmenu, setActiveSubmenu] = createSignal(0);
    const [colorClasses, setColorClasses] = createSignal('');

    // 3 states to make sure we hide it when the page is loaded
    const [searchActive, setSearchActive] = createSignal<'init' | 'active' | 'inactive'>('init');

    const location = useLocation();

    createEffect(() => {
        location.pathname && setActiveSubmenu(0);
        const headerColors = currentHeaderColors();
        const myColorClasses = headerColors?.linkColor
            ? textColorClasses[headerColors.linkColor as ColorName]
            : '';
        setColorClasses(myColorClasses);
    });

    const toggleBodyScroll = (disable: boolean) => {
        if (disable) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    };

    createEffect(() => {
        const bodyElement = document.querySelector('body');

        if (bodyElement) {
            if (searchActive() === 'active') {
                bodyElement.style.overflow = 'hidden';
            } else {
                bodyElement.style.position = 'auto';
            }
        }

        onCleanup(() => {
            toggleBodyScroll(false);
        });
    });

    const handleOnClick = (e: Event, ID: number) => {
        e.stopImmediatePropagation();
        setActiveSubmenu(activeSubmenu() === ID ? 0 : ID);
    };

    let dropdownMenuRef: HTMLDivElement | undefined;
    const handleOutsideClick = () => {
        const handleClickOutside = (event: Event) => {
            if (!dropdownMenuRef?.contains(event.target as Node) && activeSubmenu() !== 0) {
                setActiveSubmenu(0);
            }
        };

        window.addEventListener('click', handleClickOutside);

        onCleanup(() => {
            window.removeEventListener('click', handleClickOutside);
        });
    };

    onMount(() => {
        handleOutsideClick();
    });

    const buttonClasses = () =>
        clsx(
            'flex items-center rounded-s px-3 desktop:px-6 py-4 leading-none text-bigTabletMenu desktop:text-desktopMenu',
            bgHoverColorClasses[currentHeaderColors()?.buttonHoverColor as ColorName],
            bgFocusColorClasses[currentHeaderColors()?.buttonHoverColor as ColorName],
        );
    const secondaryButtonClasses = 'flex items-center rounded-s px-4 laptop:px-6 py-4 leading-none';
    const transitionClasses = 'transition-color ease-in-out duration-300';

    const getSearchWrapperClasses = () => {
        const classes = clsx(
            'fixed right-3 top-3 z-50',
            { ['animate-fade-in-from-right']: searchActive() === 'active' },
            { ['animate-fade-out-to-right']: searchActive() === 'inactive' },
            { ['hidden']: searchActive() === 'init' },
        );
        return classes;
    };

    return (
        <Show when={props.primaryMenuData} fallback={<div>No menu</div>}>
            <div
                class={clsx(
                    'flex',
                    'font-medium',
                    'justify-between',
                    'gap-2',
                    'relative',
                    'top-0',
                    'left-0',
                    'w-full',
                    'z-10',
                    'min-h-headerDesktop',
                    'px-4',
                    'laptop:px-8',
                    'text-bigTabletMenu',
                    'desktop:text-desktopMenu',
                    'desktop:justify-center',
                    colorClasses(),
                )}
            >
                <div class="flex items-center px-2 mr-0 desktop:mr-auto desktop:min-h-headerDesktop desktop:px-8 desktop:absolute desktop:left-0 desktop:top-0">
                    <A aria-label={translate('go-to-homepage')} class="py-4 desktop:px-4" href="/">
                        <SiteLogo width="7.14rem" height="2.69rem" ariaHidden={true} focusable={false} />
                    </A>
                </div>

                <nav aria-label={translate('primary-navigation')} class={middleNavClassNames}>
                    <For each={props.primaryMenuData}>
                        {(item, index) => (
                            <Switch
                                fallback={
                                    <A class={clsx(buttonClasses(), transitionClasses)} href={item.url}>
                                        {item.title}
                                    </A>
                                }
                            >
                                <Match when={item.children}>
                                    <div class="relative">
                                        <button
                                            aria-label={`${(activeSubmenu() === item.ID ? translate('hide') : translate('show'))} ${translate('submenu-for')} ${item.title}`}
                                            class={clsx(buttonClasses(), transitionClasses)}
                                            data-index={index()}
                                            onClick={(e) => handleOnClick(e, item.ID)}
                                        >
                                            {item.title}
                                            <span class={clsx('ml-[0.625rem]', 'transform', 'translate-y-[2px]')}>
                                                <ChevronUpBoldIcon
                                                    width="10px"
                                                    height="100%"
                                                    class={clsx('origin-center transform transition-transform duration-300', {
                                                        'rotate-180': activeSubmenu() === item.ID,
                                                    })}
                                                />
                                            </span>
                                        </button>

                                        <Show when={item.ID === activeSubmenu()}>
                                            <div class={clsx('absolute', 'animate-fade-in-move-up')} ref={dropdownMenuRef}>
                                                <div class={'shadow-subtle text-[1.125rem] min-w-[18rem] rounded-s bg-white p-3 text-black'}>
                                                    <ul class="submenu">
                                                        {item?.children?.map((subItem) => (
                                                            <li class="p-0">
                                                                <A
                                                                    class={clsx(
                                                                        'block',
                                                                        'p-3',
                                                                        'hover:bg-blackTransparent-06',
                                                                        'focus:bg-blackTransparent-06',
                                                                        'hover:text-primary',
                                                                        'focus:text-primary',
                                                                        'rounded-xs',
                                                                        'text-neutral-900',
                                                                        transitionClasses,
                                                                    )}
                                                                    href={subItem.url}
                                                                >
                                                                    {subItem.title}
                                                                </A>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </Show>
                                    </div>
                                </Match>
                            </Switch>
                        )}
                    </For>
                </nav>

                <nav aria-label={translate('secondary-navigation')} class={clsx(navClassNames)}>
                    <For each={props.secondaryMenuData}>
                        {(item, index) => (
                            <Show
                                fallback={
                                    <A
                                        class={clsx(
                                            secondaryButtonClasses,
                                            transitionClasses,
                                            borderColorClasses[currentHeaderColors()?.contactBorderColor as ColorName],
                                            borderHoverColorClasses[currentHeaderColors()?.contactBorderHoverColor as ColorName],
                                            borderFocusColorClasses[currentHeaderColors()?.contactBorderHoverColor as ColorName],
                                            { 'desktop:text-desktopMenuButton border-[1.5px]': index() === 0 },
                                        )}
                                        href={item.url}
                                    >
                                        {item.title}
                                    </A>
                                }
                                when={index() !== 0}
                            >
                                <A class={clsx(buttonClasses(), transitionClasses)} href={item.url}>
                                    {item.title}
                                </A>
                            </Show>
                        )}
                    </For>

                    <button onClick={() => setSearchActive('active')} aria-label={translate('search')} class={clsx(buttonClasses())}>
                        <SearchIcon />
                    </button>

                    {/* Overlay div to dim screen */}
                    <Portal mount={document.querySelector('dim') ?? undefined}>
                        <div
                            class={`fixed inset-0 z-40 overflow-hidden bg-black transition-opacity duration-500 ease-in-out ${searchActive() === 'active' ? 'opacity-50' : 'opacity-0'
                            } ${searchActive() === 'active' ? '' : 'pointer-events-none'}`}
                            onClick={() => setSearchActive('inactive')}
                        />
                        <div class={getSearchWrapperClasses()} style={{ height: 'calc(100vh - 1.5rem)', width: 'calc(100vw / 3)', bottom: '2rem' }}>
                            <MobileSearch setSearchActive={setSearchActive} />
                        </div>
                    </Portal>

                </nav>
            </div>
        </Show>
    );
};
